import React, { useState, useEffect } from 'react';
import { Clock, Search, Camera, OctagonAlert, Wrench, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import OwnEquipmentCard from 'components/cpEquipments/EquipmentCard';
import EquipmentDetailsModal from 'components/cpEquipments/EquipmentDetailsModal';
import AddEquipmentModal from 'components/cpEquipments/AddEditEquipmentsModal';
import EquipmentDeleteConfirmationModal from 'components/cpEquipments/DeleteEquipmentConfirmation';
import {
  getCrewEquipment,
  createCreatorEquipment,
  editCreatorEquipment,
  deleteCreatorEquipment,
  uploadEquipmentPhotos
} from 'services/creatorService';

const MyEquipments = () => {
  const [equipmentDetailsOpen, setEquipmentDetailsOpen] = useState(false);
  const [equipmentDetailsData, setEquipmentDetailsData] = useState(null);
  const [isAddingEquipment, setIsAddingEquipment] = useState(false);
  const [mode, setMode] = useState("add");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("shoot_request");
  const [status, setStatus] = useState("all");
  const [equipments, setEquipments] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  const itemToDelete = equipments.find(item => item.crew_equipment_id === idToDelete);
const equipmentName = itemToDelete?.equipment?.equipment_name || itemToDelete?.equipment_name || "this equipment";

  const fetchEquipments = () => {
    getCrewEquipment({})
      .then((response) => {
        if (response?.data) setEquipments(response.data);
      })
      .catch((error) => console.error("Error fetching equipment:", error));
  };

  useEffect(() => {
    fetchEquipments();
  }, []);

  const handleOpenEquipmentDetails = (equipmentId) => {
    const equipment = equipments.find(
      (item) => item.crew_equipment_id === equipmentId
    );
    if (equipment) {
      setEquipmentDetailsData(equipment); // Set selected equipment data
      setEquipmentDetailsOpen(true); // Open the modal
    }
  };

  const handleEditEquipment = (equipmentData) => {
    setEquipmentDetailsData(equipmentData);
    setMode("edit");
    setIsAddingEquipment(true);
  };

  const handleAddEquipment = () => {
    setEquipmentDetailsData(null);
    setMode("add");
    setIsAddingEquipment(true);
  };

  const handleSaveEquipment = async (payload) => {
    const response =
      mode === "add"
        ? await createCreatorEquipment(payload)
        : await editCreatorEquipment(payload);

    if (
      response?.code === 200 ||
      response?.status === 200 ||
      response?.success
    ) {
      // fetchEquipments();
      return response?.data?.crew_equipment_id || payload?.crew_equipment_id;
    }
    return null;
  };

 const handleUploadImages = async (crewEquipmentId, images) => {
  // Only call the upload API if there are actual new images
  if (images && images.length > 0 && crewEquipmentId) {
    await uploadEquipmentPhotos(crewEquipmentId, images);
  }

  // ALWAYS fetch equipments to reflect deletions or text changes
  fetchEquipments();
};

  const handleDeleteEquipment = (id) => {
    setIdToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (idToDelete) {
      const response = await deleteCreatorEquipment(idToDelete);
      if (response?.status === 200 || response?.success) {
        fetchEquipments();
      }
      setDeleteModalOpen(false);
      setIdToDelete(null);
    }
  };

  const filteredCards = equipments.filter((c) => {
  const name = c.equipment?.equipment_name || c.equipment_name || "";
  const matchesSearch = name
    .toLowerCase()
    .includes(search.trim().toLowerCase());

  let matchesStatus = true;

  if (status === "all" || status === "1") {
    matchesStatus = true;
  } else if (status === "4") {
    matchesStatus = false;
  }

  return matchesSearch && matchesStatus;
});
  return (
    <div className="md:p-6 min-h-screen dark:bg-dark-grey transition-colors duration-300">
      <div className="bg-[#F5F1EE] dark:bg-black-primary rounded-2xl border border-[#E8DDD3] dark:border-zinc-800 shadow-sm">
        <div className="p-4 md:p-6 pb-3 border-b border-[#E8DDD3] dark:border-zinc-800">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Equipment
              </h1>
              <p className="text-gray-500 dark:text-zinc-400 text-sm mt-1">
                Manage your equipment inventory and rentals.
              </p>
            </div>
            <Button
              className="bg-button-coloured dark:bg-white dark:text-black dark:hover:bg-zinc-200 gap-2"
              onClick={handleAddEquipment}
            >
              <Plus size={20} /> Add Equipment
            </Button>
          </div>

          {/* FILTER CONTROLS */}
          <div className="mt-4 flex flex-col sm:flex-row justify-between gap-3">
            {/* Search Input */}
            <div className="relative flex-1 max-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search equipment ..."
                /* UPDATED: Removed border-none, added border-transparent and dark:border-golden */
                className="pl-10 bg-white dark:bg-zinc-900 border border-transparent dark:border-golden dark:text-white dark:placeholder:text-zinc-500 shadow-sm h-10 focus-visible:ring-golden"
              />
            </div>

            {/* Dropdowns */}
            <div className="flex gap-3">
              <Select value={type} onValueChange={setType}>
                {/* UPDATED: Removed border-none, added border-transparent and dark:border-golden */}
                <SelectTrigger className="bg-white dark:bg-zinc-900 border border-transparent dark:border-golden dark:text-white shadow-sm h-10 w-[160px] focus:ring-golden">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="dark:bg-zinc-900 dark:border-zinc-800">
                  <SelectItem value="shoot_request">Shoot Request</SelectItem>
                  <SelectItem value="equipment_request">
                    Equipment Request
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select value={status} onValueChange={setStatus}>
                {/* UPDATED: Removed border-none, added border-transparent and dark:border-golden */}
                <SelectTrigger className="bg-white dark:bg-zinc-900 border border-transparent dark:border-golden dark:text-white shadow-sm h-10 w-[140px] focus:ring-golden">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="dark:bg-zinc-900 dark:border-zinc-800">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="1">Available</SelectItem>
                  <SelectItem value="4">Under Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="p-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCards.map((item) => (
              <OwnEquipmentCard
                key={item.crew_equipment_id}
                item={item}
                handleOpenEquipmentDetails={handleOpenEquipmentDetails}
                handleEditEquipment={handleEditEquipment}
                handleDeleteEquipment={handleDeleteEquipment}
              />
            ))}
          </div>
        </div>
      </div>

      {/* <EquipmentDeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      /> */}
       <EquipmentDeleteConfirmationModal
      isOpen={deleteModalOpen}
      onClose={() => setDeleteModalOpen(false)}
      onConfirm={handleConfirmDelete}
      equipmentName={equipmentName}
    />

      <EquipmentDetailsModal
        open={equipmentDetailsOpen}
        onOpenChange={setEquipmentDetailsOpen}
        equipment={equipmentDetailsData}
      />

      <AddEquipmentModal
        open={isAddingEquipment}
        onOpenChange={setIsAddingEquipment}
        mode={mode}
        equipment={equipmentDetailsData}
        onSave={handleSaveEquipment}
        onUploadImages={handleUploadImages}
      />
    </div>
  );
};

export default MyEquipments;
